import {
    isExcludeProductSelection,
    isIncludeProductSelection,
    ProductSelection
} from '../../shared/product-selection/ProductSelectionTypes';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { invalidateSelectionListAggregations } from '../../shop/selectionlists/useSelectionListAggregations';
import { invalidateSelectionListProducts } from '../../shop/selectionlists/api/useSelectionListProducts';
import { RemoveBulkByFilterRequest, removeBulkFromListByFilter } from './removeBulkFromListByFilter';
import { RemoveBulkByLineItemKeysRequest, removeBulkFromListByLineItemKeys } from './removeBulkFromListByLineItemKeys';
import { globalErrorResponse } from '../../shared/notification/actions';
import { invalidateSelectionListSummary } from './useSelectionListSummary';
import { invalidateSelectionListGroupedItems } from './useSelectionListGroupedItems';
import { convertLineItemKeysToDtoFromProductSelection } from '../../shared/utils/convertLineItemKey';
import { invalidateSelectionLists } from './useSelectionLists';
import { showNotification } from '../../shared/notification/toastNotification';
import { ProductFilter } from '../../store/api';

type Params = {
    selectionListId: string;
    productSelection: ProductSelection;
    filterRequest: ProductFilter;
};

export const getRemoveProductsRequest = (
    { selectionListId, productSelection, filterRequest }: Params,
    t: TFunction
): (() => Promise<void>) => {
    return () => {
        if (isIncludeProductSelection(productSelection)) {
            const body: RemoveBulkByLineItemKeysRequest = {
                lineItemKeys: convertLineItemKeysToDtoFromProductSelection(productSelection)
            };
            return removeBulkFromListByLineItemKeys(selectionListId, body);
        } else if (isExcludeProductSelection(productSelection)) {
            const body: RemoveBulkByFilterRequest = {
                filterRequest,
                excludedLineItemKeys: productSelection.excluded
            };
            return removeBulkFromListByFilter(selectionListId, body);
        } else {
            throw new Error(t('notifications.buildAddProductsCallback.error'));
        }
    };
};

type RemoveBulkFromListParams = {
    onSuccess: () => void;
    productSelection: ProductSelection;
    productFilter: ProductFilter;
};

export const useRemoveBulkFromSelectionList = ({
    onSuccess,
    productSelection,
    productFilter
}: RemoveBulkFromListParams) => {
    const { t } = useTranslation();
    const [isLoading, setLoading] = useState(false);

    return {
        isLoading,
        removeBulkFromList: async (listId: string) => {
            setLoading(true);
            const apiCallback = getRemoveProductsRequest(
                {
                    selectionListId: listId,
                    productSelection,
                    filterRequest: productFilter
                },
                t
            );

            try {
                await apiCallback();
                showNotification(t('notifications.removeProductsFromList.message'));
                invalidateSelectionLists().then();
                invalidateSelectionListAggregations(listId).then();
                invalidateSelectionListSummary(listId).then();
                invalidateSelectionListProducts(listId).then();
                invalidateSelectionListGroupedItems(listId).then();
                onSuccess();
            } catch (error: any) {
                globalErrorResponse(error);
                invalidateSelectionListProducts(listId).then();

                return Promise.reject(error);
            } finally {
                setLoading(false);
            }
        }
    };
};
